import { Box, Stack } from '@mui/joy';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { Image, ImagePreview } from 'core/elements';
import { getDomainUrl } from 'core/utils';
import { IPartMediaImage } from 'parts/types';
import { Color, Direction, Variant } from 'theme/enums';

interface IProps {
  images?: IPartMediaImage[];
}

const containerStyle = {
  width: '100%',
  aspectRatio: '1 / 1',
  borderRadius: 8,
  border: '1px solid',
  borderColor: 'neutral.outlinedBorder',
  position: 'relative' as const,
  overflow: 'hidden' as const,
};

// GIFs and animated WebPs lose animation, SVGs lose vector fidelity, and AVIF is not tuned
// for the raster transform — bypass the transform for these mime types.
const MIME_TYPES_BYPASS_TRANSFORM = new Set([
  'image/gif',
  'image/svg+xml',
  'image/webp',
  'image/avif',
]);

const buildImageSrc = (image: IPartMediaImage): string => {
  const base = `${getDomainUrl()}${image.url}`;

  if (image.mime && MIME_TYPES_BYPASS_TRANSFORM.has(image.mime)) {
    return base;
  }

  return `${base}/h/600/w/600/entire/1`;
};

export const ImageGallery = ({ images }: IProps) => {
  const { t } = useTranslation();

  // Show the default image first per Figma; preserve BE order for the rest.
  const validImages = useMemo(() => {
    const filtered = images?.filter((image) => image?.url) ?? [];

    return [...filtered].sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault));
  }, [images]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Scroll position is the source of truth for the active index.
  const handleScroll = useCallback(() => {
    const element = scrollerRef.current;
    if (!element || element.clientWidth === 0) {
      return;
    }

    const newIndex = Math.round(element.scrollLeft / element.clientWidth);
    setActiveIndex(newIndex);
  }, []);

  const scrollToIndex = useCallback((index: number) => {
    const element = scrollerRef.current;
    if (!element) {
      return;
    }

    element.scrollTo({ left: index * element.clientWidth, behavior: 'smooth' });
  }, []);

  const handlePrev = useCallback(() => {
    if (activeIndex > 0) {
      scrollToIndex(activeIndex - 1);
    }
  }, [activeIndex, scrollToIndex]);

  const handleNext = useCallback(() => {
    if (activeIndex < validImages.length - 1) {
      scrollToIndex(activeIndex + 1);
    }
  }, [activeIndex, scrollToIndex, validImages.length]);

  if (validImages.length === 0) {
    return null;
  }

  const activeImage = validImages[activeIndex] ?? validImages[0];
  const showNavigation = validImages.length > 1;

  return (
    <Stack sx={{ gap: 1 }}>
      <Box sx={{ ...containerStyle, backgroundColor: 'neutral.50' }}>
        <Box
          ref={scrollerRef}
          onScroll={handleScroll}
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {validImages.map((image, index) => (
            <Box
              key={image.id ?? index}
              onClick={() => setIsPreviewOpen(true)}
              sx={{
                flex: '0 0 100%',
                height: '100%',
                scrollSnapAlign: 'start',
                cursor: 'zoom-in',
              }}
            >
              <Image
                src={buildImageSrc(image)}
                alt={image.alt ?? ''}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          ))}
        </Box>

        {showNavigation && (
          <>
            <Box sx={{ position: 'absolute', left: 1, top: '50%', transform: 'translateY(-50%)' }}>
              <Button
                icon={<CaretLeft />}
                variant={Variant.OUTLINED}
                color={Color.NEUTRAL}
                onClick={handlePrev}
              />
            </Box>
            <Box sx={{ position: 'absolute', right: 1, top: '50%', transform: 'translateY(-50%)' }}>
              <Button
                icon={<CaretRight />}
                variant={Variant.OUTLINED}
                color={Color.NEUTRAL}
                onClick={handleNext}
              />
            </Box>
          </>
        )}
      </Box>

      {showNavigation && (
        <Stack
          direction={Direction.ROW}
          sx={{ gap: 1, justifyContent: 'center', alignItems: 'center', height: 20 }}
        >
          {validImages.map((image, index) => (
            <Box
              key={`dot${image.id ?? index}`}
              component="button"
              type="button"
              onClick={() => scrollToIndex(index)}
              aria-label={
                image.alt || image.name || t('labelGoToImageNumber', { number: index + 1 })
              }
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: '1px solid',
                borderColor: 'neutral.outlinedBorder',
                backgroundColor: index === activeIndex ? 'neutral.700' : 'transparent',
                cursor: 'pointer',
                padding: 0,
                '&:hover': {
                  backgroundColor: index === activeIndex ? 'neutral.700' : 'neutral.200',
                },
              }}
            />
          ))}
        </Stack>
      )}

      <ImagePreview
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fileUrl={`${getDomainUrl()}${activeImage.url}`}
        fileName={activeImage.name ?? activeImage.alt ?? ''}
        subtitle={activeImage.description}
        fileSize={activeImage.size}
      />
    </Stack>
  );
};
